import { Flex, csx } from '@vtex/admin-ui'
import type { StyleProp } from '@vtex/admin-ui'
import React, { useState } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'
import { last } from 'ramda'

import {
  SalesAssociateMovementsTrackingModalContent,
  SalesAssociateMovementsTrackingModalFooter,
  SalesAssociateMovementsTrackingModalHeader,
} from '../SalesAssociateMovementsTrackingModal'
import { OccurrencesBreadcrumb } from './OccurrencesBreadcrumb'
import { OccurrencesModalTitle } from './OccurrencesModalTitle'
import { ModalContentTemplate } from '../../shared/types'
import type { MultipleAssistancesContentTemplate_salesAssociate$key } from './__generated__/MultipleAssistancesContentTemplate_salesAssociate.graphql'
import { useOccurrencesModal } from '../../hooks/useOccurrencesModal'
import { sizeToRem } from '../../../../core/theme/theme'

const getNumberStyles = (props: { selected: boolean }): StyleProp => ({
  width: '3.5rem',
  height: '3.5rem',
  borderRadius: 3,
  backgroundColor: props.selected ? '$blue40' : '#F4F6FB',
  ':not(:last-child)': {
    marginRight: '$space-2',
  },
})

const getNumberTextStyles = (props: { selected: boolean }): StyleProp => ({
  color: props.selected ? 'white' : '$blue40',
  fontSize: sizeToRem(5),
})

export type MultipleAssistancesContentTemplateProps = {
  salesAssociateRef: MultipleAssistancesContentTemplate_salesAssociate$key
}

const ASSISTANCES_TIMES = [2, 3, 4, 5]

export const MultipleAssistancesContentTemplate = ({
  salesAssociateRef,
}: MultipleAssistancesContentTemplateProps) => {
  const [selectedNumber, setSelectedNumber] = useState<number | undefined>()

  const {
    options,
    selectedOccurrences,
    setSelectedOccurrences,
    setModalContentTemplate,
    setBreadcrumbOccurrences,
    setDisplayedOccurrences,
    breadcrumbOccurrences,
    multipleAssistancesData,
    setMultipleAssistancesData,
    close,
  } = useOccurrencesModal()

  const salesAssociateData = useFragment<MultipleAssistancesContentTemplate_salesAssociate$key>(
    graphql`
      fragment MultipleAssistancesContentTemplate_salesAssociate on SalesAssociate {
        name
        avatar {
          url
        }
      }
    `,
    salesAssociateRef
  )

  const handleBackClick = () => {
    const prevSelectedOccurrences = selectedOccurrences.slice(0, -1)

    setModalContentTemplate(ModalContentTemplate.Occurrences)
    setSelectedOccurrences((prev) => prev.slice(0, -1))
    setBreadcrumbOccurrences((prev) => prev.slice(0, -1))

    setDisplayedOccurrences(() => {
      const prevSelectedOccurrencesChildren =
        last(prevSelectedOccurrences)?.children ?? []

      return prevSelectedOccurrencesChildren.length
        ? prevSelectedOccurrencesChildren
        : options.occurrences
    })

    setMultipleAssistancesData((prev) => ({
      ...prev,
      active: false,
    }))
  }

  const handleConfirm = (assistancesCount: number) => {
    setMultipleAssistancesData((prev) => ({
      ...prev,
      assistancesCount,
    }))

    setDisplayedOccurrences(multipleAssistancesData.occurrences)
    setModalContentTemplate(ModalContentTemplate.Occurrences)
    setBreadcrumbOccurrences([])
  }

  const handleNumberClick = (value: number) => () => {
    if (value === selectedNumber) {
      handleConfirm(value)

      return
    }

    setSelectedNumber(value)
  }

  const handleConfirmClick = () => {
    if (selectedNumber) {
      handleConfirm(selectedNumber)
    }
  }

  return (
    <>
      <SalesAssociateMovementsTrackingModalHeader
        avatarUrl={salesAssociateData.avatar?.url}
        sellerName={salesAssociateData.name}
        timerDate={options.serviceStartDate}
      >
        <OccurrencesBreadcrumb occurrences={breadcrumbOccurrences} />
        <OccurrencesModalTitle
          title={options.modalTitle}
          onClick={handleBackClick}
          showGoBack={breadcrumbOccurrences.length > 0}
        />
      </SalesAssociateMovementsTrackingModalHeader>
      <SalesAssociateMovementsTrackingModalContent>
        <div
          className={csx({
            paddingY: '$space-7',
            display: 'flex',
            justifyContent: 'center',
          })}
        >
          {ASSISTANCES_TIMES.map((number) => (
            <Flex
              key={number}
              align="center"
              justify="center"
              className={csx(
                getNumberStyles({ selected: selectedNumber === number })
              )}
              onClick={handleNumberClick(number)}
              data-testid="occurrences-modal-item"
            >
              <p
                className={csx(
                  getNumberTextStyles({
                    selected: selectedNumber === number,
                  })
                )}
              >
                {number}
              </p>
            </Flex>
          ))}
        </div>
      </SalesAssociateMovementsTrackingModalContent>
      <SalesAssociateMovementsTrackingModalFooter
        onCancelClick={close}
        onConfirmClick={handleConfirmClick}
        shouldDisableConfirmButton={!selectedNumber}
      />
    </>
  )
}
